import { useEffect, useState } from "react";
import { api } from "@/lib/apiClient";
import { Trophy, Cake, Compass, Award } from "lucide-react";

/**
 * CareerPanel — Mostra Pontos de Carreira (lifetime + ano), histórico de
 * aniversários, e qualificação para próxima edição (se a janela onboarding
 * estiver aberta). Visível ao próprio consultor; reusável noutras vistas.
 */
export default function CareerPanel({ playerId = null, viewerRole = null }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const url = playerId ? `/players/${playerId}/career` : "/me/career";
    api.get(url)
      .then((r) => setData(r.data))
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, [playerId]);

  if (loading) {
    return <div className="kw-card p-6 text-[#737373] text-sm" data-testid="career-loading">A carregar carreira…</div>;
  }
  if (!data || !data.lifetime) return null;

  const { lifetime, current_year: yr, qualification, anniversary_history } = data;

  return (
    <div className="space-y-4" data-testid="career-panel">
      <div className="grid md:grid-cols-3 gap-4">
        <KpiCard
          icon={Trophy}
          color="#E50000"
          label="Carreira (vitalícia)"
          value={Math.round(lifetime.total_points)}
          sub={`${lifetime.weeks_count} semana${lifetime.weeks_count === 1 ? "" : "s"} reportadas`}
          testid="career-lifetime"
        />
        <KpiCard
          icon={Cake}
          color="#FFB020"
          label={`Ano atual${yr?.since ? ` (desde ${yr.since})` : ""}`}
          value={Math.round(yr?.total_points || 0)}
          sub={yr?.next_anniversary ? `Próx. aniversário: ${yr.next_anniversary}` : ""}
          testid="career-year"
        />
        {qualification?.active ? (
          <KpiCard
            icon={Compass}
            color="#3B82F6"
            label="Qualificação próxima edição"
            value={Math.round(qualification.total_points || 0)}
            sub={`Onboarding desde ${qualification.onboarding_start}`}
            testid="career-qualification"
          />
        ) : (
          <div className="kw-card p-5 opacity-60" data-testid="career-no-qualification">
            <div className="flex items-center gap-2 mb-2">
              <Compass className="w-4 h-4 text-[#737373]" />
              <div className="kw-overline text-[10px]">Qualificação</div>
            </div>
            <div className="text-sm text-[#737373]">
              Janela de candidaturas à próxima edição ainda não está aberta.
            </div>
          </div>
        )}
      </div>

      {anniversary_history && anniversary_history.length > 0 && (
        <div className="kw-card p-5" data-testid="anniversary-history">
          <div className="flex items-center gap-2 mb-3">
            <Award className="w-4 h-4 text-[#E50000]" />
            <div className="kw-overline">Histórico por ano de anuidade</div>
          </div>
          <div className="space-y-2">
            {anniversary_history.map((h, idx) => (
              <div key={idx} className="flex items-center justify-between border-b border-[#1A1A1A] pb-2 last:border-0 last:pb-0">
                <div className="flex-1">
                  <div className="font-semibold text-sm">
                    Ano {h.year_number}
                  </div>
                  <div className="text-[10px] uppercase tracking-widest text-[#737373]">
                    {h.period_start} → {h.period_end}
                  </div>
                </div>
                <div className="text-right">
                  <div className="font-display text-xl font-black">{Math.round(h.total_points || 0)}</div>
                  <div className="text-[9px] uppercase tracking-widest text-[#737373]">pts</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function KpiCard({ icon: Icon, color, label, value, sub, testid }) {
  return (
    <div className="kw-card p-5" data-testid={testid}>
      <div className="flex items-center gap-2 mb-2">
        <Icon className="w-4 h-4" style={{ color }} />
        <div className="kw-overline text-[10px]">{label}</div>
      </div>
      <div className="font-display text-4xl font-black" style={{ color }}>{value}</div>
      {sub && <div className="text-[10px] uppercase tracking-widest text-[#737373] mt-2">{sub}</div>}
    </div>
  );
}
